import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { isEqual } from 'lodash';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';

import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import Close from '@material-ui/icons/Close';

import { getFilterCount } from '../../services/helpers';

import colors from '../../assets/colors';
import fontSize from '../../assets/fontSize';

const StyledButton = styled(Button)`
  && {
    text-transform: none;
    justify-content: ${({ isMobile }) =>
      isMobile ? 'center' : 'space-between'};
    background-color: ${colors.white};
    color: ${({ isActive }) => isActive && colors.secondary};
    border-color: ${({ isActive }) => isActive && colors.secondary};
    font-size: ${({ isMobile }) => isMobile && fontSize.regular};
    font-family: 'Open Sans', sans-serif;
    min-width: ${({ isMobile }) => (isMobile ? 'min-content' : '100%')};
    white-space: nowrap;
  }
`;
const ApplyButton = styled(Button)`
  && {
    background-color: ${({ disabled }) =>
      disabled ? colors.grey : colors.primary};
    color: ${colors.white};
    font-family: 'Open Sans', sans-serif;
    font-weight: 500;

    :hover {
      background-color: ${colors.primary};
    }
  }
`;
const DialogTitleWrapper = styled(DialogTitle)`
  && {
    padding: 0.5rem 0.75rem;
  }
`;
const StyledDialogTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const StyledContent = styled(DialogContent)`
  && {
    padding: 0.5rem;
  }
`;
const StyledPaper = styled(Paper)`
  padding: 0.5rem 1rem;
`;
const DialogTitleText = styled.h5`
  margin: 0;
  font-size: ${fontSize.xLarge};
  font-weight: 500;
  font-family: 'Open Sans', sans-serif;
`;
const CloseButtonWrapper = styled.div`
  margin-right: 0.5rem;
`;
const FilterRow = styled.div`
  min-width: 300px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const FilterText = styled.p`
  margin: 0;
  font-size: ${fontSize.large};
`;
const StyledCheckbox = styled(Checkbox)`
  && {
    color: ${({ checked }) => checked && colors.primary};
  }
`;
const ResetButton = styled(Button)`
  && {
    color: ${({ disabled }) => (disabled ? colors.grey : colors.primary)};
    text-transform: none;
  }
`;
const LeftWrap = styled.div`
  display: flex;
  align-items: center;
`;
const CounterChip = styled.span`
  && {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    font-size: ${fontSize.regular};
    background-color: ${({ isActive }) =>
      isActive ? colors.secondary : colors.grey};
    color: ${colors.white};
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const LOCATION_TYPE = 'location';
const NO_FILTERS = 0;

const FiltersDialog = props => {
  const {
    filterTitle,
    filterCounts,
    appliedFilters,
    handleApplyFilters,
    filterType,
    filters,
    isActive,
    isMobile
  } = props;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [checkedFilters, setCheckedFilters] = useState([]);

  useEffect(() => {
    setCheckedFilters(appliedFilters);
  }, [appliedFilters]);

  const handleApply = () => {
    handleApplyFilters(filterType, checkedFilters);
    setIsDialogOpen(false);
  };

  const toggleFilter = filter => {
    if (checkedFilters.includes(filter)) {
      const newFilters = checkedFilters.filter(
        existingFilter => existingFilter !== filter
      );
      setCheckedFilters(newFilters);
    } else {
      setCheckedFilters(prev => [...prev, filter]);
    }
  };

  const resetFilters = () => {
    setCheckedFilters([]);
  };

  return (
    <>
      <StyledButton
        variant="outlined"
        size={isMobile ? 'small' : 'large'}
        endIcon={
          isMobile ? (
            <CounterChip isActive={isActive}>
              {checkedFilters.length}
            </CounterChip>
          ) : (
            <ArrowDropDown />
          )
        }
        isMobile={isMobile}
        isActive={isActive}
        onClick={() => setIsDialogOpen(true)}
      >
        {filterTitle}
      </StyledButton>
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        scroll="paper"
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitleWrapper>
          <StyledDialogTitle isMobile={isMobile}>
            <LeftWrap>
              <CloseButtonWrapper isMobile={isMobile}>
                <IconButton size="small" onClick={() => setIsDialogOpen(false)}>
                  <Close />
                </IconButton>
              </CloseButtonWrapper>
              <DialogTitleText>{filterTitle}</DialogTitleText>
            </LeftWrap>
            <ResetButton
              onClick={resetFilters}
              disabled={checkedFilters?.length === NO_FILTERS}
            >
              Reset
            </ResetButton>
          </StyledDialogTitle>
        </DialogTitleWrapper>
        <StyledContent dividers>
          <StyledPaper elevation={0}>
            {Object.keys(filters)?.map(filter => {
              return (
                <FilterRow key={filter}>
                  <FilterText>
                    {filters[filter]} (
                    {filterType === LOCATION_TYPE
                      ? getFilterCount(filterCounts, filters[filter])
                      : getFilterCount(filterCounts, filter)}
                    )
                  </FilterText>
                  <StyledCheckbox
                    onChange={() => toggleFilter(filter)}
                    checked={checkedFilters.includes(filter)}
                    color="primary"
                  />
                </FilterRow>
              );
            })}
          </StyledPaper>
        </StyledContent>
        <DialogActions>
          <ApplyButton
            onClick={handleApply}
            variant="contained"
            size="large"
            disabled={isEqual(checkedFilters, appliedFilters)}
            fullWidth
          >
            Apply
          </ApplyButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FiltersDialog;
